import { ATTENDANCE_URL } from '../constants';
import { apiSlice } from './apiSlice';

export const leaveApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Apply for leave - userId as query parameter, leave data in body
    applyForLeave: builder.mutation({
      query: ({ userId, leaveData }) => ({
        url: `${ATTENDANCE_URL}/leave?userId=${userId}`,
        method: 'POST',
        body: leaveData,
      }),
      invalidatesTags: ['Leave', 'LeaveBalance'],
    }),

    // Get leaves - all or by user ID (userId as optional query parameter)
    getLeaves: builder.query({
      query: (userId = '') => `${ATTENDANCE_URL}/leave${userId ? `?userId=${userId}` : ''}`,
      providesTags: ['Leave'],
    }),

    // Get technician leaves for supervisor (divisionId as optional query parameter)
    getLeavesForSupervisor: builder.query({
      query: (divisionId = '') => `${ATTENDANCE_URL}/leave/supervisor${divisionId ? `?divisionId=${divisionId}` : ''}`,
      providesTags: ['Leave'],
    }),

    // Get leaves for manager (managerId in path)
    getLeavesForManager: builder.query({
      query: (managerId) => `${ATTENDANCE_URL}/leave/manager/${managerId}`,
      providesTags: ['Leave'],
    }),

    // Get manager review queue (managerId in path, departmentId as optional query parameter)
    getManagerReviewBundle: builder.query({
      query: ({ managerId, departmentId = '' }) =>
        `${ATTENDANCE_URL}/leave/manager/${managerId}/review-queue${departmentId ? `?departmentId=${departmentId}` : ''}`,
      providesTags: ['Leave'],
    }),

    // Get CFM review queue (cfmId in path, academyId as optional query parameter)
    getCfmReviewBundle: builder.query({
      query: ({ cfmId, academyId = '' }) =>
        `${ATTENDANCE_URL}/leave/cfm/${cfmId}/review-queue${academyId ? `?academyId=${academyId}` : ''}`,
      providesTags: ['Leave'],
    }),

    // Get specific leave by ID (leaveId in path)
    getLeaveById: builder.query({
      query: (leaveId) => `${ATTENDANCE_URL}/leave/${leaveId}`,
      providesTags: ['Leave'],
    }),

    // Forward leave to manager (leaveId in path, data in body)
    forwardLeaveToManager: builder.mutation({
      query: ({ leaveId, supervisorId, supervisorEmail, managerId, managerEmail, note }) => ({
        url: `${ATTENDANCE_URL}/leave/${leaveId}/forward`,
        method: 'PUT',
        body: { supervisorId, supervisorEmail, managerId, managerEmail, note },
      }),
      invalidatesTags: ['Leave'],
    }),

    // ✅ CORRECTED: Forward supervisor leave to CFM (matches Java controller)
    forwardLeaveToCfm: builder.mutation({
      query: ({ leaveId, managerId, managerEmail, cfmId, cfmEmail, note }) => ({
        url: `${ATTENDANCE_URL}/leave/${leaveId}/manager/forward-to-cfm`,
        method: 'PUT',
        body: { managerId, managerEmail, cfmId, cfmEmail, note },
      }),
      invalidatesTags: ['Leave'],
    }),

    // Reject leave as supervisor (leaveId in path, data in body)
    rejectLeaveAsSupervisor: builder.mutation({
      query: ({ leaveId, supervisorId, supervisorEmail, note }) => ({
        url: `${ATTENDANCE_URL}/leave/${leaveId}/reject`,
        method: 'PUT',
        body: { supervisorId, supervisorEmail, note },
      }),
      invalidatesTags: ['Leave'],
    }),

    // Manager decision (approve/reject) - leaveId in path, data in body
    decideLeaveAsManager: builder.mutation({
      query: ({ leaveId, managerId, decision, note }) => ({
        url: `${ATTENDANCE_URL}/leave/${leaveId}/manager-decision`,
        method: 'PUT',
        body: { managerId, decision, note },
      }),
      invalidatesTags: ['Leave'],
    }),

    // CFM decision (approve/reject) - leaveId in path, data in body
    decideLeaveAsCfm: builder.mutation({
      query: ({ leaveId, decision, note }) => ({
        url: `${ATTENDANCE_URL}/leave/${leaveId}/cfm-decision`,
        method: 'PUT',
        body: { decision, note },
      }),
      invalidatesTags: ['Leave'],
    }),

    // CFM decision for manager leaves - leaveId in path, data in body
    decideManagerLeaveAsCfm: builder.mutation({
      query: ({ leaveId, decision, note }) => ({
        url: `${ATTENDANCE_URL}/leave/${leaveId}/cfm-manager-decision`,
        method: 'PUT',
        body: { decision, note },
      }),
      invalidatesTags: ['Leave'],
    }),

    // ✅ CORRECTED: Get leave balance for current year (uses JWT token)
    getLeaveBalance: builder.query({
      query: () => `${ATTENDANCE_URL}/leave-balance/current`,
      providesTags: ['LeaveBalance'],
      transformResponse: (response) => {
        // Transform backend response to consistent format
        return {
          // Handle both snake_case and camelCase from backend
          earned_leave_days: response.earned_leave_days || response.earnedLeaveDays || 0,
          casual_leave_days: response.casual_leave_days || response.casualLeaveDays || 0,
          // FIXED: Added the missing fields that your backend actually returns
          total_earned_leave_days: response.total_earned_leave_days || response.totalEarnedLeaveDays || 0,
          total_casual_leave_days: response.total_casual_leave_days || response.totalCasualLeaveDays || 0,
          total_earned_leave_hours: response.total_earned_leave_hours || response.totalEarnedLeaveHours || 0,
          total_casual_leave_hours: response.total_casual_leave_hours || response.totalCasualLeaveHours || 0,
          earned_leave_balance_remaining: response.earned_leave_balance_remaining || response.earnedLeaveBalanceRemaining || 0,
          casual_leave_balance_remaining: response.casual_leave_balance_remaining || response.casualLeaveBalanceRemaining || 0,
        };
      },
    }),

    // ✅ Get leave balance for specific calendar
    getLeaveBalanceByCalendar: builder.query({
      query: (calendarId) => {
        if (!calendarId) {
          throw new Error('Calendar ID is required');
        }
        return `${ATTENDANCE_URL}/leave-balance/calendar/${calendarId}`;
      },
      providesTags: ['LeaveBalance'],
      transformResponse: (response) => {
        // Transform backend response to consistent format
        return {
          earned_leave_days: response.earned_leave_days || response.earnedLeaveDays || 0,
          casual_leave_days: response.casual_leave_days || response.casualLeaveDays || 0,
          total_earned_leave_hours: response.total_earned_leave_hours || response.totalEarnedLeaveHours || 0,
          total_casual_leave_hours: response.total_casual_leave_hours || response.totalCasualLeaveHours || 0,
          earned_leave_balance_remaining: response.earned_leave_balance_remaining || response.earnedLeaveBalanceRemaining || 0,
          casual_leave_balance_remaining: response.casual_leave_balance_remaining || response.casualLeaveBalanceRemaining || 0,
        };
      },
    }),

    // ✅ NEW: Get CFM's own leave history (for CFM leave records page)
    getLeavesForCfm: builder.query({
      query: ({ cfmId, academyId = '' }) =>
        `${ATTENDANCE_URL}/leave/cfm/${cfmId}/leaves${academyId ? `?academyId=${academyId}` : ''}`,
      providesTags: ['Leave'],
    }),

    // ✅ NEW: Admin viewing a CFM's leave history (for admin page)
    getLeavesForCfmAsAdmin: builder.query({
      query: ({ cfmId, academyId = '', includeEscalated = false }) =>
        `${ATTENDANCE_URL}/leave/admin/cfm/${cfmId}/leaves?includeEscalated=${includeEscalated}${academyId ? `&academyId=${academyId}` : ''}`,
      providesTags: ['Leave'],
    }),

    // ✅ Admin decision for CFM leaves - leaveId in path, data in body
    decideCfmLeaveAsAdmin: builder.mutation({
      query: ({ leaveId, decision, note }) => ({
        url: `${ATTENDANCE_URL}/leave/${leaveId}/admin-decision`,
        method: 'PUT',
        body: { decision, note },
      }),
      invalidatesTags: ['Leave'],
    }),

    // ✅ NEW: Admin retrieving all leave records across the system
    getAllLeavesAsAdmin: builder.query({
      query: () => `${ATTENDANCE_URL}/leave/admin/leaves`,
      providesTags: ['Leave'],
    }),

    // ✅ NEW: Apply manual leave as manager
    applyLeaveManuallyAsManager: builder.mutation({
      query: (request) => ({
        url: `${ATTENDANCE_URL}/leave/manual`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['Leave'],
    }),

    // Get leaves by userId (paginated response for user details page)
    getLeavesByUserId: builder.query({
      query: (userId) => `${ATTENDANCE_URL}/leave/user/${userId}`,
      providesTags: ['Leave'],
    }),
  }),
});

export const {
  useApplyForLeaveMutation,
  useGetLeavesQuery,
  useGetLeavesForSupervisorQuery,
  useGetLeavesForManagerQuery,
  useGetManagerReviewBundleQuery,
  useGetCfmReviewBundleQuery,
  useGetLeaveByIdQuery,
  useForwardLeaveToManagerMutation,
  useForwardLeaveToCfmMutation,
  useRejectLeaveAsSupervisorMutation,
  useDecideLeaveAsManagerMutation,
  useDecideLeaveAsCfmMutation,
  useDecideManagerLeaveAsCfmMutation,
  useDecideCfmLeaveAsAdminMutation,
  useGetLeaveBalanceQuery,
  useGetLeaveBalanceByCalendarQuery,
  useGetLeavesForCfmQuery,
  useGetLeavesForCfmAsAdminQuery,
  useGetAllLeavesAsAdminQuery,
  useApplyLeaveManuallyAsManagerMutation,
  useGetLeavesByUserIdQuery,
} = leaveApiSlice;